import { ARIA, EVENTS, SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * @class AreasOfFocus
 * @extends MessageBus
 * @description Manages expand/collapse behaviour for each areas-of-focus card.
 * Clicking a card's footer toggles a drawer that animates upward to reveal
 * the description and CTA. Each card manages its own open/close state independently.
 */
export default class AreasOfFocus extends MessageBus {
  /**
   * @param {HTMLElement} element - The module's root container element.
   */
  constructor(element) {
    super();

    /** @type {HTMLElement} */
    this.element = element;

    /** @type {HTMLElement[]} An array of toggle elements. */
    this.toggles = [];

    this.init();
  }

  /**
   * Initialize the module and wire up handlers.
   * @returns {this} The instance for chaining.
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Cache DOM references used by the component.
   * @returns {this} The instance for chaining.
   */
  cacheDomReferences() {
    this.toggles = Array.from(
      this.element.querySelectorAll(SELECTORS.AREAS_OF_FOCUS_ITEM_TOGGLE)
    );

    return this;
  }

  /**
   * Bind event handler methods to `this`.
   * @returns {this} The instance for chaining.
   */
  setupHandlers() {
    this.handleToggleClick = this.onToggleClick.bind(this);

    return this;
  }

  /**
   * Attach event listeners and enable component behaviour.
   * @returns {this} The instance for chaining.
   */
  enable() {
    if (!this.toggles.length) return this;

    this.toggles.forEach((toggle) => {
      toggle.addEventListener(EVENTS.CLICK, this.handleToggleClick);
    });

    return this;
  }

  /**
   * Remove event listeners and clean up.
   * @returns {this} The instance for chaining.
   */
  destroy() {
    this.toggles.forEach((toggle) => {
      toggle.removeEventListener(EVENTS.CLICK, this.handleToggleClick);
    });

    return this;
  }

  /**
   * Toggle a single item's open state when its toggle is clicked.
   * @param {MouseEvent} event - Click event from the toggle element.
   * @returns {void}
   */
  onToggleClick(event) {
    const toggle = event.currentTarget;
    const item = toggle.closest(SELECTORS.AREAS_OF_FOCUS_ITEM);
    const drawer = this.element.contains(item)
      ? item.querySelector(SELECTORS.AREAS_OF_FOCUS_ITEM_DRAWER)
      : null;

    if (!item) return;

    const isOpen = toggle.getAttribute(ARIA.EXPANDED) === 'true';
    const nextState = isOpen ? 'false' : 'true';

    item.setAttribute(ARIA.EXPANDED, nextState);
    toggle.setAttribute(ARIA.EXPANDED, nextState);

    if (drawer) drawer.setAttribute(ARIA.HIDDEN, isOpen ? 'true' : 'false');
  }
}
